import { Component, effect, forwardRef, inject, Input, OnDestroy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NonNullableFormBuilder,
  ValidationErrors,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { InputComponent } from '@bn/web-ui/input/input.component';
import { TagSelectComponent } from '@bn/web-ui/standalone/selects/tag-select/tag-select.component';
import { AdvancedSettings } from './models';

@Component({
  selector: 'bn-web-ui-advanced-settings',
  templateUrl: './advanced-settings.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => AdvancedSettingsComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => AdvancedSettingsComponent),
    },
  ],
  imports: [FormsModule, ReactiveFormsModule, TagSelectComponent, InputComponent, TranslatePipe],
})
export class AdvancedSettingsComponent implements ControlValueAccessor, OnDestroy {
  #fb = inject(NonNullableFormBuilder);

  @Input()
  public readonly = false;

  startupCommand = this.#fb.control<AdvancedSettings['startupCommand']>('');
  startupCommands = this.#fb.control<AdvancedSettings['startupCommands']>([]);
  containerArgs = this.#fb.control<AdvancedSettings['containerArgs']>('');
  workingDir = this.#fb.control<AdvancedSettings['workingDir']>('');

  protected readonly subscriptions = new Subscription();
  form = this.#fb.group({
    startupCommand: this.startupCommand,
    startupCommands: this.startupCommands,
    containerArgs: this.containerArgs,
    workingDir: this.workingDir,
  });

  public isTouched = false;
  public isFocused = false;

  public onChange: (value: AdvancedSettings) => void = () => ({});
  public onTouched: () => void = () => ({});

  #startupCommandsValue = toSignal<string[] | null | undefined>(this.startupCommands.valueChanges.pipe(takeUntilDestroyed()));

  constructor() {
    this.subscriptions.add(
      this.form.valueChanges.subscribe((data) => {
        this.onChange(data as AdvancedSettings);
      }),
    );

    effect(() => {
      // Preview whole command
      this.startupCommand.setValue(this.formatOneLineCommand(this.#startupCommandsValue() ?? []), { emitEvent: false });
    });
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public writeValue(value: AdvancedSettings): void {
    if (value) {
      const { startupCommands, containerArgs, workingDir } = value;

      this.form.patchValue({
        startupCommand: this.formatOneLineCommand(startupCommands),
        startupCommands,
        containerArgs,
        workingDir,
      });
    }
  }

  public registerOnChange(fn: (value: AdvancedSettings) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  public onTouch(): void {
    this.isFocused = false;

    if (this.isTouched) {
      return;
    }

    this.isTouched = true;
    this.onTouched();
  }

  public onFocus(): void {
    this.isFocused = true;
  }

  public validate(): ValidationErrors | null {
    return this.startupCommand.valid && this.startupCommands.valid && this.containerArgs.valid && this.workingDir.valid
      ? null
      : {
          invalidForm: { valid: false, message: 'Advanced settings are invalid' },
        };
  }

  private formatOneLineCommand(commands: string[] = []): string {
    return commands.length ? `"${commands.join('" & "')}"` : '';
  }
}
